/**
 * Client-side mirror of server/api-util/orgRoles.js.
 *
 * MUST stay in sync with the server file. The server is the authoritative
 * gate — these constants exist for UX (show/hide buttons, role labels in
 * the team UI, etc.). Never use them to make a security decision in
 * isolation; always pair with a server check.
 *
 * Spec: UPDATE.md §3.6.5
 */

export const ROLES = Object.freeze({
  OWNER: 'owner',
  ADMIN: 'admin',
  MANAGER: 'manager',
  MEMBER: 'member',
});

export const ROLE_HIERARCHY = Object.freeze({
  [ROLES.OWNER]: 4,
  [ROLES.ADMIN]: 3,
  [ROLES.MANAGER]: 2,
  [ROLES.MEMBER]: 1,
});

export const ALL_ROLES = Object.freeze([
  ROLES.OWNER,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.MEMBER,
]);

export const ROLE_LABELS = Object.freeze({
  [ROLES.OWNER]: 'Owner',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.MEMBER]: 'Member',
});

export const isValidRole = role => ALL_ROLES.includes(role);

export const PERMISSIONS = Object.freeze({
  VIEW_DASHBOARD: [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER, ROLES.MEMBER],
  CREATE_SHOWS: [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER],
  EDIT_ANY_SHOW: [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER],
  DELETE_SHOWS: [ROLES.OWNER, ROLES.ADMIN],
  ASSIGN_CREATORS: [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER],
  PUBLISH_EPISODES: [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER],
  CREATE_RESEARCH: [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER], // postponed
  VIEW_INSIGHTS: [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER, ROLES.MEMBER],
  VIEW_RENTED_AUDIENCE: [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER],
  SEND_NEWSLETTERS: [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER],
  INVITE_TEAM: [ROLES.OWNER, ROLES.ADMIN],
  REMOVE_TEAM: [ROLES.OWNER, ROLES.ADMIN],
  CHANGE_ROLES: [ROLES.OWNER, ROLES.ADMIN],
  VIEW_BILLING: [ROLES.OWNER],
  MANAGE_SUBSCRIPTION: [ROLES.OWNER],
  DELETE_ORG: [ROLES.OWNER],
});

export const roleHasPermission = (role, permission) => {
  const allowed = PERMISSIONS[permission];
  if (!allowed) return false;
  return allowed.includes(role);
};

export const canInviteRole = (actorRole, targetRole) => {
  if (!roleHasPermission(actorRole, 'INVITE_TEAM')) return false;
  if (!isValidRole(targetRole)) return false;
  if (targetRole === ROLES.OWNER) return false;
  return ROLE_HIERARCHY[targetRole] < ROLE_HIERARCHY[actorRole];
};

export const canChangeRole = (actorRole, actorUserId, targetUserId, currentTargetRole, newRole) => {
  if (!roleHasPermission(actorRole, 'CHANGE_ROLES')) return false;
  if (actorUserId === targetUserId) return false;
  if (currentTargetRole === ROLES.OWNER) return false;
  if (newRole === ROLES.OWNER) return false;
  if (!isValidRole(newRole)) return false;
  if (ROLE_HIERARCHY[currentTargetRole] >= ROLE_HIERARCHY[actorRole]) return false;
  if (ROLE_HIERARCHY[newRole] >= ROLE_HIERARCHY[actorRole]) return false;
  return true;
};

export const canRemoveRole = (actorRole, actorUserId, targetUserId, targetRole) => {
  if (!roleHasPermission(actorRole, 'REMOVE_TEAM')) return false;
  if (actorUserId === targetUserId) return false;
  if (targetRole === ROLES.OWNER) return false;
  if (!isValidRole(targetRole)) return false;
  return ROLE_HIERARCHY[targetRole] < ROLE_HIERARCHY[actorRole];
};
